// crewbit-admin/src/components/money/clients/OverridePill.tsx
//
// Tier 2-UI Phase D-admin §WorldClass.1 — §0.13 override-discipline primitive.
// Mirrors crewbit-mobile/components/money/clients/OverridePill.tsx body
// decisions per `feedback_admin_pair_must_match_mobile_body` — same 4 source
// variants, same tone semantics, same click-to-tooltip affordance. Web idiom
// uses inline-CSS chrome (admin convention) instead of StyleSheet.
//
// Renders the per-field source provenance ("From Acme Corp's default" /
// "From your defaults" / "Default" / "Custom for this invoice") with a click
// affordance opening OverridePillTooltip carrying the reasoning string.

import { palette, radii, spacing, typography } from '../../../styles/tokens';
import type { ClientDefaultsSource } from '../../../types/clientDefaults';

interface Props {
  source: ClientDefaultsSource;
  clientName?: string | null;
  onClick?: () => void;
}

interface Tone {
  bg: string;
  dot: string;
  fg: string;
}

const TONE: Record<ClientDefaultsSource, Tone> = {
  client_default: {
    bg: palette.accentWash,
    dot: palette.accent,
    fg: palette.accentDeep,
  },
  business_default: {
    bg: palette.g100,
    dot: palette.g500,
    fg: palette.g600,
  },
  hardcoded: {
    bg: palette.g100,
    dot: palette.g400,
    fg: palette.g500,
  },
  invoice_override: {
    bg: palette.amberWash,
    dot: palette.amber,
    fg: palette.amber,
  },
};

function labelFor(source: ClientDefaultsSource, clientName?: string | null): string {
  switch (source) {
    case 'client_default':
      return `From ${clientName ?? 'this client'}`;
    case 'business_default':
      return 'From your defaults';
    case 'hardcoded':
      return 'Default';
    case 'invoice_override':
      return 'Custom for this invoice';
  }
}

export default function OverridePill({ source, clientName, onClick }: Props) {
  const tone = TONE[source];
  const label = labelFor(source, clientName);
  const interactive = !!onClick;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: tone.bg,
        color: tone.fg,
        border: 'none',
        borderRadius: radii.sm,
        paddingInline: spacing.xs,
        paddingBlock: 3,
        fontSize: 11,
        fontWeight: typography.weight.semibold,
        fontFamily: typography.fontFamily,
        cursor: interactive ? 'pointer' : 'default',
        maxWidth: 220,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: tone.dot,
          flexShrink: 0,
        }}
      />
      {label}
    </button>
  );
}
